// The subpath, not the package root: a value import of the root pulls the
// whole of @volley/data — Prisma included — into the browser bundle.
import type { AskVolleyTurn } from "@volley/data/ask-volley";
import { MAX_ASK_VOLLEY_THREAD_TURNS } from "@volley/data/ask-volley";

import type { ThreadMessage } from "./types";

/**
 * The prior turns to send with an ask.
 *
 * `ThreadMessage` is what the overlay renders; an `AskVolleyTurn` is what the
 * models are shown. Only the variants carrying prose survive the conversion:
 *
 *  - `carousel` has no prose at all. What was served is already on the server's
 *    own record of that turn, so replaying card titles would be telling it
 *    something it knows, in a form it did not write.
 *  - `loading` is a spinner.
 *  - `support` is an assistant turn: we wrote it, and "I couldn't find an
 *    article on that" is exactly the context a follow-up needs.
 *  - `rating` carries prose only once it has been answered no. That is the
 *    whole value of a thumb down: the retry knows the last answer missed, so it
 *    can avoid re-serving it. A thumb up says nothing the next question needs.
 *
 * Trimmed to the last {@link MAX_ASK_VOLLEY_THREAD_TURNS}, which is what the
 * request schema accepts — a longer thread would be rejected outright rather
 * than truncated, so the trim happens here instead.
 */
export function toTurns(thread: ThreadMessage[]): AskVolleyTurn[] {
    const turns = thread.flatMap<AskVolleyTurn>((message) => {
        if (message.kind === "user") {
            return [{ role: "user", text: message.content }];
        }
        if (message.kind === "assistant" || message.kind === "support") {
            return [{ role: "assistant", text: message.content }];
        }
        if (message.kind === "rating" && message.helpful === false) {
            return [{ role: "user", text: "That answer didn't help." }];
        }
        return [];
    });

    return turns.slice(-MAX_ASK_VOLLEY_THREAD_TURNS);
}
